import { apiService } from '@shared/services/apiServices';
import certificateEndpoints from './certificateEndpoints';
import type {
  ExportCertificatesParams,
  ExportCertificatesResponse,
} from '../types/myCertificateTypes';

/**
 * Requests a PDF export of the certificate list for the authenticated
 * caller, scoped and filtered identically to `listCertificates` (minus
 * pagination — export always covers every matching row).
 *
 * @param params - optional filters: facilityIds, startDate, endDate, certificateId
 * @returns A short-lived presigned download URL, or `null` on error.
 * @throws When the response is a 403 (Forbidden) or 404 (no matching records) —
 *   callers should distinguish these from a generic failure.
 */
export async function exportCertificates(
  params: ExportCertificatesParams = {}
): Promise<ExportCertificatesResponse['data']> {
  const qs = new URLSearchParams();
  if (params.facilityIds && params.facilityIds.length > 0) {
    qs.append('facilityIds', params.facilityIds.join(','));
  }
  if (params.equipmentIds && params.equipmentIds.length > 0) {
    qs.append('equipmentIds', params.equipmentIds.join(','));
  }
  if (params.technicianId) {
    qs.append('technicianId', params.technicianId);
  }
  if (params.technicianIds && params.technicianIds.length > 0) {
    qs.append('technicianIds', params.technicianIds.join(','));
  }
  if (params.startDate) {
    qs.append('startDate', params.startDate);
  }
  if (params.endDate) {
    qs.append('endDate', params.endDate);
  }
  if (params.certificateId) {
    qs.append('certificateId', params.certificateId);
  }
  if (params.sortOrder) {
    qs.append('sortOrder', params.sortOrder);
  }

  const endpoint = `${certificateEndpoints.EXPORT}${qs.toString() ? `?${qs.toString()}` : ''}`;

  const response = await apiService.getAPI(endpoint, {
    skipGlobalErrorRedirect: true,
  });
  if (response.error) {
    if (response.status === 403) {
      throw Object.assign(new Error('Forbidden'), { status: 403 });
    }
    if (response.status === 404) {
      throw Object.assign(new Error('No certificates to export'), {
        status: 404,
      });
    }
    throw new Error('Failed to export certificate list');
  }

  const payload = (response.data ?? {}) as Record<string, unknown>;
  const inner = (payload['data'] ?? payload) as Record<string, unknown>;
  const url = inner['download_url'];
  if (typeof url !== 'string') {
    throw new Error('Failed to export certificate list');
  }

  return {
    download_url: url,
    expires_in: Number(inner['expires_in'] ?? 0),
    total: Number(inner['total'] ?? 0),
  };
}
